import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetUsersQuery,
  GetUsersData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetUsersState
  extends MergeRequestState<GetUsersData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getUsers,
  isFetching: (state: ReduxState) => state.server.getUsers.isFetching,
  data: (state: ReduxState) => state.server.getUsers.data,
  error: (state: ReduxState) => state.server.getUsers.error,
};

const initialState: GetUsersState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getUsers';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { query, config }: { query: GetUsersQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getUsers(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetUsers {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getUsers: ServerGetUsers = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
